/**
 * Footer Component
 */

'use client';

import styled from '@emotion/styled';
import { Text } from '@template-project/afrodita-components';
import { isAllowedHref } from '@/lib/utils/is-allowed-href';

const FooterContainer = styled.footer`
  background-color: ${({ theme }) => theme.colors.background.primary};
  border-top: 1px solid ${({ theme }) => theme.colors.border.color};
  padding: ${({ theme }) => theme.space['2xl']} ${({ theme }) => theme.space.lg};
  margin-top: ${({ theme }) => theme.space['3xl']};
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const LinksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.space['2xl']};
  margin-bottom: ${({ theme }) => theme.space['2xl']};

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.space.xl};
  }
`;

const LinkColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.md};
`;

const ColumnTitle = styled(Text)`
  margin-bottom: ${({ theme }) => theme.space.sm};
`;

const FooterLink = styled.a`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding-top: ${({ theme }) => theme.space.xl};
  border-top: 1px solid ${({ theme }) => theme.colors.border.color};
`;

const CopyrightText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

interface FooterLinkData {
  text: string;
  href: string;
}

interface FooterColumn {
  title: string;
  links: Array<FooterLinkData>;
}

interface FooterProps {
  columns?: FooterColumn[];
  brandName?: string;
}

const DEFAULT_COLUMNS: FooterColumn[] = [
  {
    title: 'Shop',
    links: [
      { text: 'Products', href: '/products' },
      { text: 'Collections', href: '/collections' },
      { text: 'Search', href: '/search' },
    ],
  },
  {
    title: 'Company',
    links: [
      { text: 'About Us', href: '/about' },
      { text: 'Contact', href: '#' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { text: 'Privacy Policy', href: '#' },
      { text: 'Terms of Service', href: '#' },
    ],
  },
];

export function Footer({ columns = DEFAULT_COLUMNS, brandName = 'Shapermint' }: FooterProps) {
  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer>
      <FooterContent>
        <LinksGrid>
          {columns.map((column) => (
            <LinkColumn key={column.title}>
              <ColumnTitle variant="body" weight="bold" size="small">
                {column.title}
              </ColumnTitle>
              {column.links.map((link) => {
                const safeHref = isAllowedHref(link.href) ? link.href : undefined;
                return (
                  <FooterLink key={link.href + link.text} href={safeHref}>
                    {link.text}
                  </FooterLink>
                );
              })}
            </LinkColumn>
          ))}
        </LinksGrid>

        <Copyright>
          <CopyrightText>
            © {currentYear} {brandName}. All rights reserved.
          </CopyrightText>
        </Copyright>
      </FooterContent>
    </FooterContainer>
  );
}

